import { gql, useQuery } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import Badge from '@orn/shared-web/components/Badge';
import { CalendarIcon } from '@orn/shared-web/icons';
import formatAccountLabel from '@orn/shared-web/utils/account/formatAccountLabel';
import { useParams } from 'react-router-dom';

import { NuxGoalClientFactKind } from '../../__generated__/globalTypes';
import Entity from '../../components/entity';
import RecordAlertBanner from '../../components/entity/RecordAlertBanner';
import RecordPane, { useCloseRecord } from '../../components/entity/RecordPane';
import RecentTransactionsSection from '../../components/records/RecentTransactionsSection';
import RecurringStreamSection, { RecurringStreamSectionBase } from '../../components/records/RecurringStreamSection';
import { JsonDebugRow } from '../../components/records/rows';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import { useRecordPageVisit } from '../../hooks/useRecordPageVisit';
import { PATHS } from '../../paths';
import { usePathWithDateRange } from '../../providers/DateRangeProvider';
import { entityHref } from '../../utils/entityDestination';
import { getTimingPill } from '../../utils/records/timingPill';

import { buildRecurringTransactionHero, resolveRecurringTransactionPage } from './recurringTransactionPage';

import type {
  GetRecurringTransactionPageQuery,
  GetRecurringTransactionPageQueryVariables,
} from './__generated__/RecurringTransactionPane.gql';
import type { IRecordHeroMetaCell } from '../../components/entity/RecordPane';

export const GET_RECURRING_TRANSACTION_PAGE = gql`
  query GetRecurringTransactionPage($id: ID!, $includeRawJson: Boolean!) {
    getRecurringTransaction(id: $id) {
      id
      description
      isActive
      nextOccurrence
      amountRange
      currencyCode
      rawJson @include(if: $includeRawJson)
      merchant {
        id
        ...EntityAvatar
      }
      financialAccount {
        id
        name
        mask
        ...EntityAvatar
      }
      lastTransaction {
        id
        date
        amount
        rawDescription
        financialAccount {
          id
          name
          mask
          ...EntityAvatar
        }
      }
      workItem {
        ...RecordAlertBanner
      }
      ...RecurringStreamSection
    }
  }
  ${Entity.Avatar.Fragment}
  ${RecordAlertBanner.Fragment}
  ${RecurringStreamSectionBase.Fragment}
`;

interface IRecurringTransactionPaneProps {
  /** Stories pass this directly; production rendering falls back to the route param. */
  recurringTransactionId?: string;
}

/**
 * The recurring stream record page at
 * `/recurring-transactions/:recurringTransactionId`, on the record shell.
 *
 * The hero has two modes keyed on whether a payment exists: the stream's last
 * payment (its amount, date, and account), or — for a stream with none — the
 * expected next one (the typical range and the predicted date). An inactive
 * stream keeps whichever layout applies but drops every prediction. The body
 * is the stream's active alert, its stream section, and its recent payments.
 * The last payment's raw description closes the page when it differs from the
 * name. The raw JSON is selected, and its row drawn, only when
 * `FeatureGate.DebugTransactionDetails` is on.
 */
const RecurringTransactionPane = ({
  recurringTransactionId: recurringTransactionIdProp,
}: IRecurringTransactionPaneProps = {}) => {
  const params = useParams();
  const recurringTransactionId = recurringTransactionIdProp ?? params.recurringTransactionId ?? '';
  const closeRecord = useCloseRecord(PATHS.RECURRING_TRANSACTIONS);
  const addDateRangeToPath = usePathWithDateRange();
  const showDebugJson = useFeatureGate(FeatureGate.DebugTransactionDetails);

  const { data, loading, error } = useQuery<
    GetRecurringTransactionPageQuery,
    GetRecurringTransactionPageQueryVariables
  >(GET_RECURRING_TRANSACTION_PAGE, {
    variables: { id: recurringTransactionId, includeRawJson: showDebugJson },
    skip: !recurringTransactionId,
  });

  const page = resolveRecurringTransactionPage({ id: recurringTransactionId, data, loading, error });
  useRecordPageVisit(NuxGoalClientFactKind.PageVisitedRecurring, page.status === 'ready' ? page.stream.id : undefined);
  if (page.status !== 'ready') {
    return (
      <RecordPane
        status={page.status}
        onClose={closeRecord}
        notFoundMessage={
          recurringTransactionId ? "This recurring payment isn't available." : 'No recurring payment was specified.'
        }
      />
    );
  }

  const { stream } = page;
  const hero = buildRecurringTransactionHero(stream);
  const timing = hero.pill && hero.pill !== 'inactive' ? getTimingPill(hero.pill, { variant: 'recurring' }) : null;
  const meta: IRecordHeroMetaCell[] = [];
  if (hero.dateLabel) {
    meta.push({ label: 'Date', value: hero.dateLabel, icon: <CalendarIcon /> });
  }
  if (hero.account) {
    meta.push({
      label: 'Account',
      value: formatAccountLabel(hero.account),
      leading: <Entity.Avatar entity={hero.account} size="sm" />,
      to: addDateRangeToPath(entityHref({ type: 'financialAccount', id: hero.account.id })),
    });
  }

  return (
    <RecordPane
      status="ready"
      onClose={closeRecord}
      hero={
        <RecordPane.Hero
          statusPill={
            hero.pill === 'inactive' ? (
              <Badge type="pill-color" size="lg" color="gray">
                Inactive
              </Badge>
            ) : timing ? (
              <Badge type="pill-color" size="lg" color={timing.color}>
                {timing.label}
              </Badge>
            ) : undefined
          }
          logoSrc={hero.logoSrc}
          name={hero.name}
          to={
            stream.merchant ? addDateRangeToPath(entityHref({ type: 'merchant', id: stream.merchant.id })) : undefined
          }
          showRecurringBadge
          amount={hero.amount}
          meta={meta}
          onClose={closeRecord}
        />
      }
      footer={hero.footer ?? undefined}
    >
      <RecordAlertBanner workItem={stream.workItem} />
      <RecurringStreamSection recurringTransaction={stream} amount={stream.lastTransaction?.amount ?? null} />
      <RecentTransactionsSection recurringTransactionId={stream.id} merchantId={stream.merchant?.id} />
      {showDebugJson && stream.rawJson && (
        <RecordPane.Section title="Debug">
          <JsonDebugRow label="Recurring transaction JSON" rawJson={stream.rawJson} />
        </RecordPane.Section>
      )}
    </RecordPane>
  );
};

export default RecurringTransactionPane;
